import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  GenerateAlertsQueryResponse,
  GenerateAlertsQueryParams,
} from "../types/GenerateAlerts"

type GenerateAlertsClient = typeof client<
  GenerateAlertsQueryResponse,
  Error,
  never
>
type GenerateAlerts = {
  data: GenerateAlertsQueryResponse
  error: Error
  request: never
  pathParams: never
  queryParams: GenerateAlertsQueryParams
  headerParams: never
  response: Awaited<ReturnType<GenerateAlertsClient>>
  client: {
    parameters: Partial<Parameters<GenerateAlertsClient>[0]>
    return: Awaited<ReturnType<GenerateAlertsClient>>
  }
}
export const generateAlertsQueryKey = (params: GenerateAlerts["queryParams"]) =>
  [{ url: "/v1/filter/generate-alerts" }, ...(params ? [params] : [])] as const
export type GenerateAlertsQueryKey = ReturnType<typeof generateAlertsQueryKey>
export function generateAlertsQueryOptions<
  TData = GenerateAlerts["response"],
  TQueryData = GenerateAlerts["response"],
>(
  params: GenerateAlerts["queryParams"],
  options: GenerateAlerts["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    GenerateAlerts["response"],
    GenerateAlerts["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = generateAlertsQueryKey(params)
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<GenerateAlerts["data"], GenerateAlerts["error"]>(
        {
          method: "get",
          url: `/v1/filter/generate-alerts`,
          params,
          ...options,
        }
      )
      return res
    },
  }
}
/**
 * @summary Generate alerts
 * @link /v1/filter/generate-alerts
 */
export function useGenerateAlerts<
  TData = GenerateAlerts["response"],
  TQueryData = GenerateAlerts["response"],
  TQueryKey extends QueryKey = GenerateAlertsQueryKey,
>(
  params: GenerateAlerts["queryParams"],
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        GenerateAlerts["response"],
        GenerateAlerts["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: GenerateAlerts["client"]["parameters"]
  } = {}
): UseQueryResult<TData, GenerateAlerts["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? generateAlertsQueryKey(params)
  const query = useQuery<
    GenerateAlerts["data"],
    GenerateAlerts["error"],
    TData,
    any
  >({
    ...generateAlertsQueryOptions<TData, TQueryData>(params, clientOptions),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, GenerateAlerts["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
